import { useCallback, useMemo, useState } from "react"

import ConfirmationModal, {
  ModalInfo,
} from "components/Modals/ConfirmationModal"
import { getChannels, getLocationsPerChannel } from "./utils"
import { queryCache, useMutation } from "react-query"

import { AddInitialChannels } from "./AddInitialChannels"
import { Channels } from "./Channels"
import { Locations } from "./Locations"
import { RateCardDetails } from "models/ratecard"
import { Rates } from "./Rates"
import { css } from "@emotion/css"
import { deleteBaseRate } from "api/ratecard"
import { useI18n } from "providers/I18n"
import { useSendFeedBack } from "components/FeedbackSnackbar"

type BaseRateProps = {
  rateCardId: string
}

function Base(props: BaseRateProps) {
  const { rateCardId } = props
  const { t } = useI18n()
  const sendFeedback = useSendFeedBack()
  const [modalInfo, setModalInfo] = useState<ModalInfo>()

  const params = useMemo(
    () =>
      new URLSearchParams({
        rateCardId,
      }),
    [rateCardId],
  )

  // ** get base rates
  const rateCard = queryCache.getQueryData<RateCardDetails>([
    "rate-card-details",
    rateCardId,
  ])

  // ** remove base rates
  const [remove] = useMutation(deleteBaseRate, {
    onSuccess: () => {
      setModalInfo(undefined)
      queryCache.invalidateQueries("rate-card-details")
    },
    onError: (e) => {
      setModalInfo(undefined)
      sendFeedback(t(e.message), "error")
    },
  })

  const supplierId = rateCard?.supplierId ?? ""
  const mediaTypeId = rateCard?.mediaTypeId ?? ""

  //#region Channels
  const channels = getChannels(rateCard?.supplierProducts ?? [])
  const firstChannel = channels[0].id ?? ""
  const [activeChannel, setActiveChannel] = useState<string>(() => firstChannel)
  const onActiveChannelChange = (id: string) => {
    setActiveChannel(id)
    setActiveLocation(firstLocation)
  }
  //#endregion

  //#region Locations
  const locations = rateCard?.locations ?? []
  const firstLocation = locations[0]?.location ?? ""
  const locationsPerChannel = getLocationsPerChannel(locations)
  const activeLocations = locationsPerChannel[activeChannel] ?? []

  const [activeLocation, setActiveLocation] = useState<string>(
    () => firstLocation,
  )
  const onActiveLocationChange = (id: string) => setActiveLocation(id)
  //#endregion

  const handleDelete = useCallback(
    (channelId: string, location?: string) => {
      params.set("supplierProductId", channelId)
      !!location && params.set("location", location)
      const tab = !!location ? "location" : "channel"
      setModalInfo({
        title: t(`admin.${tab}.delete.title`),
        type: "primary_destructive",
        description: t(`admin.${tab}.delete.description`),
        submitLabel: t(`admin.${tab}.delete.action`),
        onSubmit: async () =>
          await remove(params.toString()).then(() => {
            setActiveChannel(firstChannel)
            setActiveLocation(firstLocation)
            params.delete("location")
          }),
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [remove, params, t],
  )

  return (
    <div
      className={css`
        min-height: 400px;
        padding: 24px 0;
      `}
    >
      <Channels
        channels={channels}
        rateCardId={rateCardId}
        supplierId={supplierId}
        mediaTypeId={mediaTypeId}
        active={activeChannel}
        onActiveChange={onActiveChannelChange}
        deletable={true}
        onDelete={handleDelete}
      />

      <Locations
        locations={activeLocations}
        rateCardId={rateCardId}
        supplierId={supplierId}
        channelId={activeChannel}
        active={activeLocation}
        onActiveChange={onActiveLocationChange}
        deletable={true}
        onDelete={handleDelete}
      />

      <Rates
        rateCardId={rateCardId}
        channelId={activeChannel}
        location={activeLocation}
      />

      <ConfirmationModal
        onCancel={() => setModalInfo(undefined)}
        open={Boolean(modalInfo)}
        {...modalInfo}
      />
    </div>
  )
}

export default function BaseRateBase(props: BaseRateProps) {
  const { rateCardId } = props

  const rateCard = queryCache.getQueryData<RateCardDetails>([
    "rate-card-details",
    rateCardId,
  ])

  // if no data yet
  // return early to avoid flashing the "wizzard" view
  if (rateCard?.supplierProducts === undefined) {
    return (
      <div
        className={css`
          min-height: 400px;
          padding: 24px 0;
        `}
      />
    )
  }

  const supplierId = rateCard?.supplierId ?? ""
  const mediaTypeId = rateCard?.mediaTypeId ?? ""
  const channels = rateCard?.supplierProducts ?? []

  // If channels are empty show the "wizzard" view
  // else show the tabs view

  if (channels.length === 0) {
    return (
      <AddInitialChannels
        rateCardId={rateCardId}
        supplierId={supplierId}
        mediaTypeId={mediaTypeId}
      />
    )
  } else {
    return <Base rateCardId={rateCardId} />
  }
}
